import React, { useState } from 'react';
import { StandardReference } from '../../models/Standards/standard-reference';
import { StandardLot } from '../../models/Standards/standard-lot';
import { CrudFunctions } from '../../utilities/crud-functions';
import { StandardUpdateState } from '../../models/enums';
import DmaFooter from '../DmaFooter';
import { ModalMetadata } from '../../models/modal-metadata';
import { ModalMessageType } from '../../models/enums';
import GlobalDialog from '../Modal/GlobalDialog';

import DataGrid, {
    Column,
    FilterRow,
    Pager,
    Paging,
    Selection,
    HeaderFilter,
    Scrolling,
    Toolbar,
    SearchPanel,
    Export,
    Item as ToolbarItem
} from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import { LoadPanel } from 'devextreme-react';
import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';

export default function EpRefStandardsForm() {
    const [epRefStandards, setEpRefStandards] =
        useState<StandardReference[]>();

    const [epRefStandardsNewLots, setEpRefStandardsNewLots] =
        useState<StandardLot[]>();

    const [isLoading, setIsLoading] =
        useState<boolean>(false);

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    return (
        <div className="pw-content">
            <div className="lq-title-bar">
                <div className="lq-main-title">EP Reference Standards Maintenance</div>
                <div className="title-bar-spacer" />
                <div className="save-toolbar">
                    <div>
                        <Button className="title-bar-button"
                            id="goBackButton" text="" icon="arrowleft" type="normal"
                            hint="go back"
                            onClick={goBack} />
                    </div>
                </div>
            </div>

            <div className="pw-comp-row">
                <div>
                    <Button id="getEpCatalogButton" className="pw-row-button-08" height='1.6rem'
                        type="success" text="Get EP Catalog" onClick={getEpDailyCatalog} />
                </div>
                <div className="pw-margin-left-05">
                    <Button id="updateDatabaseButton" className="pw-row-button-08" height='1.6rem'
                        disabled={!epRefStandards}
                        type="success" text="Update Database" onClick={onUpdateReferencesToDatabase} />
                </div>
            </div>

            <LoadPanel visible={isLoading} />
            <DataGrid id="epCatalogDataGrid"
                width="100%" height="26rem"
                dataSource={epRefStandards ?
                    epRefStandards : []}
                wordWrapEnabled={true}
                allowColumnResizing={true}
                columnResizingMode='nextColumn'
                onExporting={onExporting}
                showBorders={true}>

                <Selection mode={'single'} />
                <HeaderFilter visible={true} />
                <FilterRow visible={true} />
                <SearchPanel visible={true} />
                <Export enabled={true} allowExportSelectedData={true} />
                <Scrolling showScrollbar='always' rowRenderingMode='virtual' />
                <Paging enabled={true} defaultPageSize={50} />
                <Pager visible={true} showInfo={true}
                    displayMode="full"
                    showNavigationButtons={true}
                    allowedPageSizes={[50, 100, "all"]} />

                <Column dataField='deviationFromDmaDatabase' caption="" width='4rem'
                    cellRender={deviationFromDmaDatabaseDatagridCellRender} />

                <Column dataField='catalogNumber' caption="Catalog#" dataType="string" width='8rem' />
                <Column dataField='productName' dataType="string" />
                <Column dataField='currentLot' caption='Current Lot #' dataType="string" width='6rem'
                    cellRender={currentLotDatagridCellRender} />
                <Column dataField='casNumber' caption='CAS#' dataType="string" width='8rem' />
                
                <Column dataField='netWeight' width='6rem' dataType="number" />
                <Column dataField='netWeightUomString' caption="UOM" dataType="string" width='6rem' />
                <Column dataField='storageString' dataType="string" width='8rem' />
                <Column dataField='monographCode' caption="Monograph" dataType="string" width='8rem' />

                <Column cellRender={epStandardCertificateRenderer} caption="COA" width="6rem" />
                <Column cellRender={epStandardWebpageRender} caption="Website" width="6rem" />

                <Toolbar>
                    <ToolbarItem location='before'>
                        <div className="pw-datagrid-toolbar-template">
                            <div className="pw-datagrid-toobar-title">EP Reference Standards</div>
                        </div>
                    </ToolbarItem>
                    <ToolbarItem name="exportButton" />
                    <ToolbarItem name="searchPanel" />
                </Toolbar>
            </DataGrid>

            <div>
                <DataGrid id="epCatalogNewLotsDataGrid"
                    width="100%" height="20rem"
                    dataSource={epRefStandardsNewLots ?
                        epRefStandardsNewLots : []}
                    wordWrapEnabled={true}
                    allowColumnResizing={true}
                    columnResizingMode='nextColumn'
                    showBorders={true}>

                    <Selection mode={'single'} />
                    <Scrolling showScrollbar='always' />
                    <Paging enabled={false} />
                    <HeaderFilter visible={true} />
                    <FilterRow visible={true} />
                    <SearchPanel visible={true} />

                    <Column dataField='updateState' caption="" width='4rem'
                        cellRender={epChangesUpdateStateDatagridCellRender} />
                    <Column dataField='catalogNumber' width='8rem' />
                    <Column dataField='productName' width='20rem' />
                    <Column dataField='currentLot' width='6rem' />
                    <Column dataField='newLot' width='6rem' />
                    
                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">EP Standard Changes</div>
                            </div>
                        </ToolbarItem>
                        <ToolbarItem name="searchPanel" />
                    </Toolbar>
                </DataGrid>
            </div>

            <DmaFooter />

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div>
    );

    async function onUpdateReferencesToDatabase() {
        if (epRefStandards) {
            await updateStandardReferences(epRefStandards)
                .then((data) => {
                    //add toast
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Standard References");
                });
        }
    }

    async function getEpDailyCatalog() {
        setIsLoading(true);
        await CrudFunctions.getData("externaldata/epcatalog")
            .then(data => {
                console.log("EP:");
                console.log(data);

                setEpRefStandards(data);

                const newLots = data.filter((d: any) => d.deviationFromDmaDatabase === true);

                console.log("newLots:");
                console.log(newLots);

                setEpRefStandardsNewLots(newLots);               

            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving EP Daily Catalog");
            })
            .finally(() => setIsLoading(false));
    }

    async function updateStandardReferences(standardReferences: StandardReference[]) {
        return await CrudFunctions.postData("standards/standardreferences", standardReferences);
    }

    //navigation

    function goBack() {
        history.back();
    }

    //render

    function deviationFromDmaDatabaseDatagridCellRender(e: any) {
        if (e.data && e.data.deviationFromDmaDatabase) {
            return (
                <div>
                    <div className="item-main"><i className="dx-icon-warning pw-red"></i></div>
                </div>
            );
        }
        else
            return (<div></div>);
    }

    function currentLotDatagridCellRender(e: any) {
        if (e.data && e.data.deviationFromDmaDatabase) {
            return (
                <div>
                    <div className="pw-red">{e.data.currentLot}</div>
                </div>
            );
        }
        else
            return (
                <div>
                    <div>{e.data.currentLot}</div>
                </div>
            );
    }

    function epChangesUpdateStateDatagridCellRender(e: any) {
        if (e.data && e.data.updateState) {
            switch (e.data.updateState) {
                case StandardUpdateState.NewStandard:
                    return <div><div className="item-main"><i className="dx-icon-add pw-green"></i></div></div>;
                case StandardUpdateState.CurrentLotChange:
                    return <div><div className="item-main"><i className="dx-icon-clock pw-orange"></i></div></div>;
                case StandardUpdateState.Discontinued:
                    return <div><div className="item-main"><i className="dx-icon-warning pw-red"></i></div></div>;
                case StandardUpdateState.Planned:
                    return <div><div className="item-main"><i className="dx-icon-eyeopen pw-blue"></i></div></div>;
            }
        }
        else
            return (<div></div>);
    }

    function epStandardCertificateRenderer(e: any) {
        //console.log("epStandardCertificateRenderer");
        //console.log(e);
        
        if (e.data && e.data.catalogNumber && e.data.currentLot) {
            const urlRef = `${e.data.catalogNumber}_${e.data.currentLot}`; 

            let url = `https://crs.edqm.eu/db/4DCGI/leaflet?leaflet=${urlRef}.pdf`;
            
            return <a href={url} rel="noopener noreferrer" target="_blank">Certificate</a>;
        }
        else
            return <div>NA</div>;
    }

    function epStandardWebpageRender(e: any) {
        if (e.data && e.data.catalogNumber && e.data.currentLot) {
            let url = `https://crs.edqm.eu/db/4DCGI/View=${e.data.catalogNumber}`;

            return <a href={url} rel="noopener noreferrer" target="_blank">Website</a>;
        }
        else
            return <div>NA</div>;
    }

    function onExporting(e: any) {
        const name = "EP Reference Standards";
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('EP');

        exportDataGrid({
            component: e.component,
            worksheet,
            autoFilterEnabled: true,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${name}.xlsx`);
            });
        });

        e.cancel = true;
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }
}


